import { ERROR_MESSAGE_500, ERROR_STATUS_500 } from "./const";
import { handleHttpError, HttpError } from "./errors";
import { HttpResponse, Options, OptionsParams } from "./type";
import {
  buildCredentials,
  buildFullPath,
  buildHeaders,
  buildPathWithSearchParams,
  buildRequestBody,
  resolvePath,
} from "./utils";
import * as Sentry from "@sentry/react";

/** 通信処理を共通化した関数 */
async function http<T>(path: string, config: RequestInit): Promise<HttpResponse<T>> {
  const request = new Request(
    // NOTE: NEXT_PUBLIC_API_HOSTだとDocker側に上書きされて使えなくなる
    buildFullPath(process.env.PUBLIC_API_URL ?? "http://127.0.0.1:3000", path),
    config,
  );

  try {
    const res = await fetch(request);

    if (!res) {
      throw new HttpError(ERROR_STATUS_500, ERROR_MESSAGE_500);
    }

    if (!res.ok) {
      const data = await res.json();

      throw new HttpError(res.status, data?.message ?? res.statusText, data);
    }

    // statusCodeが204のときにres.json()を実行するとエラーになるため
    if (res.status === 204) return { data: {}, headers: res.headers } as HttpResponse<T>;

    return { data: await res.json(), headers: res.headers } as HttpResponse<T>;
  } catch (error) {
    const httpError = handleHttpError(error);
    Sentry.captureException(httpError);
    throw httpError;
  }
}

export async function get<T, U extends OptionsParams<"get"> = OptionsParams<"get">>(
  path: string,
  options?: Options<U>,
): Promise<HttpResponse<T>> {
  const endpoint = resolvePath(path, options?.params?.path);
  return http<T>(buildPathWithSearchParams(endpoint, options?.params?.query), {
    headers: buildHeaders(options?.headers),
    credentials: buildCredentials(options?.credentials),
  });
}

export async function post<T, U, V extends OptionsParams<"post"> = OptionsParams<"post">>(
  path: string,
  body: T,
  options?: Options<V>,
): Promise<HttpResponse<U>> {
  const endpoint = resolvePath(path, options?.params?.path);
  return http<U>(buildPathWithSearchParams(endpoint, options?.params?.query), {
    method: "POST",
    headers: buildHeaders(options?.headers),
    body: buildRequestBody(body),
    credentials: buildCredentials(options?.credentials),
  });
}

export async function put<T, U, V extends OptionsParams<"put"> = OptionsParams<"put">>(
  path: string,
  body: T,
  options?: Options<V>,
): Promise<HttpResponse<U>> {
  const endpoint = resolvePath(path, options?.params?.path);
  return http<U>(buildPathWithSearchParams(endpoint, options?.params?.query), {
    method: "PUT",
    headers: buildHeaders(options?.headers),
    body: buildRequestBody(body),
    credentials: buildCredentials(options?.credentials),
  });
}

// deleteはJSの予約語であるためdestroyとする
export async function destroy<T, U, V extends OptionsParams<"delete"> = OptionsParams<"delete">>(
  path: string,
  body: T,
  options?: Options<V>,
): Promise<HttpResponse<U>> {
  const endpoint = resolvePath(path, options?.params?.path);
  return http<U>(buildPathWithSearchParams(endpoint, options?.params?.query), {
    method: "DELETE",
    headers: buildHeaders(options?.headers),
    body: buildRequestBody(body),
    credentials: buildCredentials(options?.credentials),
  });
}
